import { useEffect, useState } from "react";
import { getMapsApiKey } from "../services/Config.service";

// The key is fetched once per session and cached on success. Failures are not
// cached, so reopening the form retries. A successful key short-circuits.
let cachedKey: string | undefined;
let pendingFetch: Promise<string | null> | undefined;

const fetchKey = (): Promise<string | null> => {
  if (cachedKey) return Promise.resolve(cachedKey);
  if (!pendingFetch) {
    pendingFetch = getMapsApiKey()
      .then((res) => {
        pendingFetch = undefined;
        if (res.data) cachedKey = res.data;
        return res.data || null;
      })
      .catch(() => {
        pendingFetch = undefined;
        return null;
      });
  }
  return pendingFetch;
};

export interface IMapsApiKey {
  apiKey: string | null;
  loading: boolean;
}

// `enabled` gates the request entirely: non-superadmin callers pass false and
// never hit the backend, so the key is never requested for them.
const useMapsApiKey = (enabled: boolean): IMapsApiKey => {
  const [apiKey, setApiKey] = useState<string | null>(cachedKey ?? null);
  const [loading, setLoading] = useState<boolean>(enabled && !cachedKey);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(!cachedKey);
    void fetchKey().then((key) => {
      if (mounted) {
        setApiKey(key);
        setLoading(false);
      }
    });
    return () => {
      mounted = false;
    };
  }, [enabled]);

  return { apiKey, loading };
};

export default useMapsApiKey;
